// Reads dates, times, repeats, tags and projects out of a to-do title, in English and German. A port of the kit's
// QuickParser.swift, which is the reference: the cases in cases/parser.json run against both.
//
// The kit's patterns are NSRegularExpression (ICU) patterns. JavaScript's regular expressions differ in ways that
// matter here, so the patterns are written with these stand-ins (all with the "iu" flags):
// - ICU's \s is [\t\n\f\r\p{Z}]; JavaScript's also takes \v and U+FEFF. WS below is ICU's.
// - ICU's \d is any decimal digit (\p{Nd}); JavaScript's only 0-9. DIGIT below is ICU's. Swift's Int() still reads
//   only 0-9, so a title with other digits matches the pattern and is then left alone, in both.
// - \b doesn't know letters like "ü" in JavaScript. The kit doesn't use \b either: whole words are told by
//   lookarounds with \p{L}, which work the same in both.

import type { Day, RepeatRule, Start, Weekday } from "./types";
import type { HabitSchedule } from "./records/habit";
import { addDays, addMonths, dayFrom, weekdayOf, yearOf } from "./parser/calendar";
import {
  friendly,
  habitTitle,
  repeatTitle,
  somedayLabel,
  timeLabel,
  todayLabel,
  type LabelLanguage,
  type LabelOptions,
} from "./parser/labels";
import { firstDay } from "./parser/repeats";

/** A project the parser can file a to-do into with "@name". */
export interface ParserProject {
  id: string;
  title: string;
  /** A completed project is not offered. */
  completed?: boolean;
}

/** An area the parser can file a to-do into with "@name". */
export interface ParserArea {
  id: string;
  title: string;
}

export interface ParserContext {
  /** The browser's date: "tomorrow" is the day after it. */
  today: Day;
  /** The tags the user has, so "#errands" keeps the spelling of "Errands". */
  tags?: readonly string[];
  projects?: readonly ParserProject[];
  areas?: readonly ParserArea[];
  /** Tokens the user has taken back (their ids: the text in lower case); they stay in the title. */
  ignoring?: Iterable<string>;
  /** The language of the chips' labels; the parser reads English and German whatever it is. Default "en". */
  language?: LabelLanguage;
  /** The locale for dates and times in labels. Default: the language. */
  locale?: string;
  /** The weekday a week starts on (1 = Sunday), which orders the days in a weekly rule's label. Default Monday. */
  firstWeekday?: Weekday;
  /** Whether "3 times a week" is read as a habit: only where one can be made. Otherwise the words stay. */
  habits?: boolean;
}

/** Something recognised in a title while typing, such as "tomorrow", "5pm" or "#Calls" (QuickToken). */
export type QuickToken =
  | TokenOf<"start", Start>
  | TokenOf<"time", { hour: number; minute: number }>
  | TokenOf<"deadline", Day>
  | TokenOf<"repeats", { rule: RepeatRule; start?: Day }>
  /** "3 times a week": the new to-do becomes a habit with this How Often (docs/63 question 9). */
  | TokenOf<"habit", HabitSchedule>
  | TokenOf<"tag", string>
  | TokenOf<"project", string>
  | TokenOf<"area", string>;

interface TokenOf<K extends string, V> {
  kind: K;
  value: V;
  /** The words in the title that produced this token. */
  text: string;
  /** What the chip shows. */
  label: string;
}

export interface QuickParseResult {
  tokens: QuickToken[];
  /** The title without the recognised words. */
  title: string;
}

/** A token's identity, which `ignoring` holds: its words in lower case. */
export function tokenID(token: Pick<QuickToken, "text">): string {
  return token.text.toLowerCase();
}

// MARK: - Pattern pieces

const WS = "[\\t\\n\\f\\r\\p{Z}]";
const DIGIT = "\\p{Nd}";

const weekdays: Record<string, Weekday> = {
  sunday: 1, sonntag: 1,
  monday: 2, mon: 2, montag: 2,
  tuesday: 3, tue: 3, tues: 3, dienstag: 3,
  wednesday: 4, wed: 4, mittwoch: 4,
  thursday: 5, thu: 5, thur: 5, thurs: 5, donnerstag: 5,
  friday: 6, fri: 6, freitag: 6,
  saturday: 7, samstag: 7,
};

const months: Record<string, number> = {
  jan: 1, january: 1, januar: 1, feb: 2, february: 2, februar: 2,
  mar: 3, march: 3, "mär": 3, "märz": 3, apr: 4, april: 4,
  may: 5, mai: 5, jun: 6, june: 6, juni: 6, jul: 7, july: 7, juli: 7,
  aug: 8, august: 8, sep: 9, sept: 9, september: 9,
  oct: 10, october: 10, okt: 10, oktober: 10, nov: 11, november: 11,
  dec: 12, december: 12, dez: 12, dezember: 12,
};

const ordinals: Record<string, number> = {
  first: 1, "1st": 1, ersten: 1, "1.": 1,
  second: 2, "2nd": 2, zweiten: 2, "2.": 2,
  third: 3, "3rd": 3, dritten: 3, "3.": 3,
  fourth: 4, "4th": 4, vierten: 4, "4.": 4,
  last: -1, letzten: -1,
};

/** Longest first, so that "monday" is tried before "mon". */
function alternatives(keys: string[]): string {
  return [...keys].sort((a, b) => b.length - a.length || a.localeCompare(b)).map(escape).join("|");
}

function escape(word: string): string {
  return word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const weekdayPattern = `(${alternatives(Object.keys(weekdays))})`;
const monthPattern = `(${alternatives(Object.keys(months))})\\.?`;
const unitPattern = "days?|weeks?|months?|years?|tage?n?|wochen?|monate?n?|jahre?n?";

/**
 * "3 times", "3x", "3×", "twice", "three times", "3-mal", "3 mal", "dreimal", "drei Mal", then "a", "per", "pro", "die",
 * "im" and the like, and a week or a month.
 */
const habitNumbers: Record<string, number> = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7,
  ein: 1, zwei: 2, drei: 3, vier: 4, "fünf": 5, sechs: 6, sieben: 7,
};
const habitPattern = (() => {
  const words = alternatives(Object.keys(habitNumbers));
  const times = `(?:${DIGIT}{1,2}${WS}*(?:x|×|times|-?${WS}?mal)|(?:${words})${WS}+times|once|twice|(?:${words})${WS}*-?${WS}?mal)`;
  return `(${times})${WS}+(?:a|per|each|every|pro|die|je|in${WS}+der|im|jede|jeden)${WS}+(weeks?|months?|woche|monat)`;
})();

/** The number in "3 times", "twice" or "dreimal". */
function habitCount(words: string | undefined): number | undefined {
  if (words === undefined) return undefined;
  const lower = words.toLowerCase();
  if (lower === "once") return 1;
  if (lower === "twice") return 2;
  const digits = /^[0-9]+/.exec(lower);
  if (digits) return Number(digits[0]);
  return Object.entries(habitNumbers).find(([word]) => lower.startsWith(word))?.[1];
}

const monthPositionPatterns: string[] = (() => {
  const ordinal = `(first|second|third|fourth|last|1st|2nd|3rd|4th|ersten|zweiten|dritten|vierten|letzten|1\\.|2\\.|3\\.|4\\.)`;
  const day = `(${alternatives([...Object.keys(weekdays), "day", "tag"])})`;
  return [
    `(?:every|each)${WS}+${ordinal}${WS}+${day}(?:${WS}+of${WS}+(?:the|every|each)${WS}+month)?`,
    `(?:on${WS}+)?(?:the${WS}+)?${ordinal}${WS}+${day}${WS}+of${WS}+(?:every|each)${WS}+month`,
    `(?:jeden|jede|jedes)${WS}+${ordinal}${WS}+${day}(?:${WS}+(?:im|des|eines|jedes)${WS}+monats?)?`,
    `(?:am${WS}+)?${ordinal}${WS}+${day}${WS}+(?:jedes|eines${WS}+jeden)${WS}+monats`,
  ];
})();

/**
 * Days of the month (docs/67), with the groups of the interval (if any) and of the list of days. A day followed by a
 * weekday, a unit or a month is something else ("every 2nd day", "jeden 2. Tag", "jeden 15. Mai").
 */
const monthDaysPatterns: [string, number | undefined, number][] = (() => {
  const english = `${DIGIT}{1,2}(?:st|nd|rd|th)`;
  const englishList = `${english}(?:${WS}*(?:,|and|&|\\+)${WS}*(?:the${WS}+)?${english})*`;
  const german = `${DIGIT}{1,2}\\.`;
  const germanList = `${german}(?:${WS}*(?:,|und|&|\\+)${WS}*(?:am${WS}+)?${german})*`;
  const words = alternatives([...Object.keys(weekdays), ...Object.keys(months)]);
  const notEnglish = `(?!${WS}+(?:of|day|days|week|weeks|month|months|year|years|weekday|weekdays|workday|workdays|${words})(?!\\p{L}))`;
  const notGerman = `(?!${WS}+(?:tag|tage|tagen|woche|wochen|monat|monate|monats|jahr|jahre|werktag|arbeitstag|${words})(?!\\p{L}))`;
  return [
    [`(?:every|each)${WS}+(?:(${DIGIT}{1,2})${WS}+months?${WS}+on${WS}+|month${WS}+on${WS}+)?(?:the${WS}+)?(${englishList})(?:${WS}+(?:day${WS}+)?of${WS}+(?:the|every|each)${WS}+month)?${notEnglish}`, 1, 2],
    [`monthly${WS}+on${WS}+(?:the${WS}+)?(${englishList})${notEnglish}`, undefined, 1],
    [`(?:on${WS}+)?(?:the${WS}+)?(${englishList})${WS}+(?:day${WS}+)?of${WS}+(?:every|each)${WS}+month`, undefined, 1],
    [`(?:jeden|jede|jedes)${WS}+(?:monat${WS}+)?(?:am${WS}+)?(${germanList})(?:${WS}+(?:des|eines|jedes|im)${WS}+monats?)?${notGerman}`, undefined, 1],
    [`alle${WS}+(${DIGIT}{1,2})${WS}+monate?n?${WS}+am${WS}+(${germanList})${notGerman}`, 1, 2],
    [`monatlich${WS}+(?:am${WS}+)?(${germanList})${notGerman}`, undefined, 1],
    [`(?:am${WS}+)?(${germanList})${WS}+(?:jedes|jeden|eines${WS}+jeden)${WS}+monats`, undefined, 1],
  ];
})();

// MARK: - Matching

interface Match {
  index: number;
  length: number;
  /** The groups by number; undefined where a group took no part. */
  groups: (string | undefined)[];
}

/** The title is parsed on every keystroke; compiling the patterns each time would cost most of that. */
const compiled = new Map<string, RegExp>();

function matches(pattern: string, text: string, boundaries = true): Match[] {
  // Whole words only, and not the start of something like "monday.com"; nor part of a word joined by a hyphen
  // ("Monday-themed", "Morgen-Routine"). Digits may be joined ("14:00-15:00" still reads 14:00).
  const bounded = boundaries
    ? `(?<![\\p{L}${DIGIT}@#])(?<!\\p{L}-)(?:${pattern})(?![\\p{L}${DIGIT}]|\\.[\\p{L}${DIGIT}]|-\\p{L})`
    : pattern;
  let regex = compiled.get(bounded);
  if (!regex) {
    regex = new RegExp(bounded, "giu");
    compiled.set(bounded, regex);
  }
  return Array.from(text.matchAll(regex), (match) => ({
    index: match.index,
    length: match[0].length,
    groups: Array.from(match),
  }));
}

/** ICU's \S: anything but its \s. */
const NONSPACE = "[^\\t\\n\\f\\r\\p{Z}]";

/** An email address, a link with its scheme or "www.", or a domain with a path. */
const linkPattern = [
  `[\\p{L}${DIGIT}._%+-]+@[\\p{L}${DIGIT}-]+(?:\\.[\\p{L}${DIGIT}-]+)+`,
  `(?:https?://|www\\.)${NONSPACE}+`,
  `[\\p{L}${DIGIT}-]+(?:\\.[\\p{L}${DIGIT}-]+)+/${NONSPACE}*`,
].join("|");

// A day right after "every". ICU's $ also matches before a line terminator that ends the text.
const afterEvery = new RegExp(
  `(?:^|[^\\p{L}])(?:every|each|jeden|jede|jedes)${WS}+(?:$|(?:\\r\\n|[\\n\\v\\f\\r\\u0085\\u2028\\u2029])$)`,
  "iu",
);

/** A number as Swift's Int() reads it: ASCII digits only. */
function int(text: string | undefined): number | undefined {
  return text !== undefined && /^[0-9]+$/.test(text) ? Number(text) : undefined;
}

function unit(word: string | undefined): RepeatRule["unit"] | undefined {
  if (word === undefined) return undefined;
  const lower = word.toLowerCase();
  if (lower.startsWith("day") || lower.startsWith("tag")) return "day";
  if (lower.startsWith("week") || lower.startsWith("woche")) return "week";
  if (lower.startsWith("month") || lower.startsWith("monat")) return "month";
  if (lower.startsWith("year") || lower.startsWith("jahr")) return "year";
  return undefined;
}

/** The next day with this weekday, never today. */
function nextWeekday(weekday: Weekday, today: Day): Day {
  const distance = (weekday - weekdayOf(today) + 7) % 7;
  return addDays(today, distance === 0 ? 7 : distance);
}

function hour24(hour: number, meridiem: string | undefined): number {
  switch (meridiem?.toLowerCase()) {
    case "pm":
      return hour < 12 ? hour + 12 : hour;
    case "am":
      return hour === 12 ? 0 : hour;
    default:
      return hour;
  }
}

/** A day and month, in the coming twelve months unless a year is given. */
function date(day: string | undefined, month: number | undefined, year: string | undefined, today: Day): Day | undefined {
  const number = int(day);
  if (number === undefined || month === undefined || number < 1 || number > 31 || month < 1 || month > 12) {
    return undefined;
  }
  const currentYear = yearOf(today);
  const typed = int(year);
  // A year that isn't read (other digits than 0-9) counts as this year, and doesn't move on, as in the kit.
  let result = dayFrom(typed === undefined ? currentYear : typed < 100 ? 2000 + typed : typed, month, number);
  if (year === undefined && result < today) result = dayFrom(currentYear + 1, month, number);
  return result;
}

/** Each date pattern with how it reads its match: a start, or undefined when the words are no date after all. */
type DateResolver = (groups: (string | undefined)[], today: Day) => Start | undefined;
type TimeResolver = (groups: (string | undefined)[]) => [number, number] | undefined;

const on = (day: Day): Start => ({ kind: "on", day });

const datePatterns: [string, DateResolver][] = [
  ["(?:this evening|tonight|heute abend)", (_, today) => on(today)],
  ["(?:day after tomorrow|übermorgen)", (_, today) => on(addDays(today, 2))],
  ["(?:today|heute)", (_, today) => on(today)],
  ["(?:tomorrow|tmrw|morgen)", (_, today) => on(addDays(today, 1))],
  ["(?:next week|nächste woche)", (_, today) => on(nextWeekday(2, today))],
  ["(?:someday|irgendwann)", () => ({ kind: "someday" })],
  [
    `in${WS}+(${DIGIT}{1,3})${WS}+(${unitPattern})`,
    (groups, today) => {
      const count = int(groups[1]);
      const by = unit(groups[2]);
      if (count === undefined || by === undefined) return undefined;
      switch (by) {
        case "day":
          return on(addDays(today, count));
        case "week":
          return on(addDays(today, count * 7));
        case "month":
          return on(addMonths(today, count));
        case "year":
          return on(addMonths(today, count * 12));
      }
    },
  ],
  [
    `(?:(?:on|next|am|nächsten|nächste)${WS}+)?${weekdayPattern}`,
    (groups, today) => {
      const weekday = weekdays[(groups[1] ?? "").toLowerCase()];
      return weekday === undefined ? undefined : on(nextWeekday(weekday, today));
    },
  ],
  [
    `(?:(?:on|am)${WS}+)?(${DIGIT}{1,2})\\.(${DIGIT}{1,2})\\.(${DIGIT}{4}|${DIGIT}{2})?`,
    (groups, today) => {
      const day = date(groups[1], int(groups[2]), groups[3], today);
      return day === undefined ? undefined : on(day);
    },
  ],
  [
    `(?:on${WS}+)?${monthPattern}${WS}+(${DIGIT}{1,2})(?:st|nd|rd|th)?`,
    (groups, today) => {
      const day = date(groups[2], months[(groups[1] ?? "").toLowerCase()], undefined, today);
      return day === undefined ? undefined : on(day);
    },
  ],
  [
    `(?:(?:on|am)${WS}+)?(${DIGIT}{1,2})(?:st|nd|rd|th|\\.)?${WS}+${monthPattern}`,
    (groups, today) => {
      const day = date(groups[1], months[(groups[2] ?? "").toLowerCase()], undefined, today);
      return day === undefined ? undefined : on(day);
    },
  ],
];

const timePatterns: [string, TimeResolver][] = [
  [
    `(?:(?:at|um)${WS}+)?(${DIGIT}{1,2}):(${DIGIT}{2})${WS}*(am|pm)?(?:${WS}*uhr)?`,
    (groups) => {
      const hour = int(groups[1]);
      const minute = int(groups[2]);
      return hour === undefined || minute === undefined ? undefined : [hour24(hour, groups[3]), minute];
    },
  ],
  [
    `(?:(?:at|um)${WS}+)?(${DIGIT}{1,2})${WS}*(am|pm)`,
    (groups) => {
      const hour = int(groups[1]);
      return hour === undefined ? undefined : [hour24(hour, groups[2]), 0];
    },
  ],
  [
    `(?:um${WS}+)?(${DIGIT}{1,2})${WS}*uhr`,
    (groups) => {
      const hour = int(groups[1]);
      return hour === undefined ? undefined : [hour, 0];
    },
  ],
  [
    `(?:at|um)${WS}+(${DIGIT}{1,2})`,
    (groups) => {
      const hour = int(groups[1]);
      return hour === undefined ? undefined : [hour, 0];
    },
  ],
  [`(?:at${WS}+)?noon`, () => [12, 0]],
];

const dailyRules: Record<string, RepeatRule> = {
  daily: { interval: 1, unit: "day", afterCompletion: false },
  "täglich": { interval: 1, unit: "day", afterCompletion: false },
  weekly: { interval: 1, unit: "week", afterCompletion: false },
  "wöchentlich": { interval: 1, unit: "week", afterCompletion: false },
  monthly: { interval: 1, unit: "month", afterCompletion: false },
  monatlich: { interval: 1, unit: "month", afterCompletion: false },
  yearly: { interval: 1, unit: "year", afterCompletion: false },
  annually: { interval: 1, unit: "year", afterCompletion: false },
  "jährlich": { interval: 1, unit: "year", afterCompletion: false },
};

// MARK: - Comparing words as Foundation does

/** Case-insensitive and canonically equivalent, close to Foundation's caseInsensitiveCompare. */
function sameIgnoringCase(a: string, b: string): boolean {
  const fold = (text: string) => text.normalize("NFC").toUpperCase().toLowerCase();
  return fold(a) === fold(b);
}

/** The first letter in upper case, the rest as typed (String.capitalizedFirst). */
function capitalizedFirst(text: string): string {
  const [first = "", ...rest] = Array.from(text);
  return first.toUpperCase() + rest.join("");
}

// Foundation's whitespacesAndNewlines, which trims the title: Z*, U+0009 to U+000D and U+0085.
const edges = /^[\p{Z}\t\n\v\f\r\u0085]+|[\p{Z}\t\n\v\f\r\u0085]+$/gu;
const runsOfSpace = new RegExp(`${WS}{2,}`, "gu");

// MARK: - Parsing

export function parse(text: string, context: ParserContext): QuickParseResult {
  const today = context.today;
  const ignored = new Set(context.ignoring ?? []);
  const language = context.language ?? "en";
  const labels: LabelOptions = {
    language,
    locale: context.locale ?? language,
    firstWeekday: context.firstWeekday ?? 2,
  };
  const found: { index: number; length: number; token: QuickToken }[] = [];
  // Words inside an email address or a link belong to it: "anna@example.com" files nothing into a project
  // called "Example", and "todolix.app/today" plans nothing for today.
  const links = matches(linkPattern, text, false);

  function add(match: Match, token: Omit<QuickToken, "text">): void {
    const end = match.index + match.length;
    const overlaps = (item: { index: number; length: number }) =>
      item.index < end && match.index < item.index + item.length;
    if (found.some(overlaps) || links.some(overlaps)) return;
    const full = { ...token, text: text.slice(match.index, end) } as QuickToken;
    if (ignored.has(tokenID(full))) return;
    // One value per kind: the first date wins, the first time wins, and so on. A project and an area are one place,
    // and a habit's number of times is the repeat.
    const place = (kind: QuickToken["kind"]) => (kind === "area" ? "project" : kind === "habit" ? "repeats" : kind);
    const isDuplicate = found.some(
      (item) => place(item.token.kind) === place(full.kind) && item.token.kind !== "tag" && full.kind !== "tag",
    );
    if (!isDuplicate) found.push({ index: match.index, length: match.length, token: full });
  }

  function addRepeat(match: Match, rule: RepeatRule, start: Day | undefined): void {
    const value = start === undefined ? { rule } : { rule, start };
    add(match, { kind: "repeats", value, label: repeatTitle(rule, labels) });
  }

  // Deadlines first, so that "by Friday" is not read as a start date.
  for (const [pattern, resolve] of datePatterns) {
    for (const match of matches(`(?:due|by|until|bis)${WS}+` + pattern, text)) {
      const start = resolve(match.groups, today);
      if (start?.kind === "on") {
        add(match, { kind: "deadline", value: start.day, label: friendly(start.day, today, labels) });
      }
    }
  }

  // "3 times a week", "twice a month", "3x pro Woche", "dreimal im Monat": a habit's number of times (docs/63
  // question 9). Before the repeats, so "3 times every week" is no weekly repeat.
  if (context.habits) {
    for (const match of matches(habitPattern, text)) {
      const count = habitCount(match.groups[1]);
      const unitWord = match.groups[2]?.toLowerCase();
      if (count === undefined || unitWord === undefined) continue;
      const isWeek = unitWord.startsWith("week") || unitWord.startsWith("woche");
      if (count < 1 || count > (isWeek ? 7 : 31)) continue;
      const schedule: HabitSchedule = { kind: isWeek ? "timesPerWeek" : "timesPerMonth", count };
      add(match, { kind: "habit", value: schedule, label: habitTitle(schedule, labels) });
    }
  }

  // "every third Monday", "the last Friday of every month", "last day of each month",
  // "jeden ersten Montag im Monat", "am letzten Tag jedes Monats". Before the weekdays, which would read
  // "every last Friday" as every Friday.
  for (const pattern of monthPositionPatterns) {
    for (const match of matches(pattern, text)) {
      const ordinal = ordinals[(match.groups[1] ?? "").toLowerCase()];
      const dayWord = match.groups[2]?.toLowerCase();
      if (ordinal === undefined || dayWord === undefined) continue;
      const weekday = weekdays[dayWord];
      // A day of the month exists only as the first or the last.
      if (weekday === undefined && ordinal !== 1 && ordinal !== -1) continue;
      const position = weekday === undefined ? { ordinal } : { ordinal, weekday };
      const rule: RepeatRule = { interval: 1, unit: "month", afterCompletion: false, monthPosition: position };
      addRepeat(match, rule, firstDay(rule, today));
    }
  }
  // "every 15th", "every month on the 1st and 15th", "monthly on the 15th", "on the 15th of every month",
  // "jeden 15.", "jeden Monat am 1. und 15.", "monatlich am 15.", "am 15. jedes Monats", "alle 2 Monate am 15."
  // (docs/67). After the places in the month, which keep "every 2nd Tuesday" and "jeden 1. Montag".
  for (const [pattern, intervalGroup, daysGroup] of monthDaysPatterns) {
    for (const match of matches(pattern, text)) {
      const numbers = [...(match.groups[daysGroup] ?? "").matchAll(/\p{Nd}{1,2}/gu)].map((found) => Number(found[0]));
      if (numbers.length === 0 || !numbers.every((day) => day >= 1 && day <= 31)) continue;
      const interval = intervalGroup === undefined ? 1 : int(match.groups[intervalGroup]) ?? 1;
      const rule: RepeatRule = { interval: Math.max(interval, 1), unit: "month", afterCompletion: false, monthDays: [...new Set(numbers)].sort((a, b) => a - b) };
      addRepeat(match, rule, firstDay(rule, today));
    }
  }
  for (const match of matches("(?:every weekday|weekdays|werktags|jeden werktag)", text)) {
    const rule: RepeatRule = { interval: 1, unit: "week", afterCompletion: false, weekdays: [2, 3, 4, 5, 6] };
    addRepeat(match, rule, firstDay(rule, today));
  }
  // "every Monday", "every Mon, Wed and Fri", "jeden Montag und Donnerstag"
  const weekdayList = `${weekdayPattern}(?:${WS}*(?:,|&|\\+|and|und)${WS}*${weekdayPattern})*`;
  for (const match of matches(`(?:every|each|jeden|jede|jedes)${WS}+` + weekdayList, text)) {
    const words = text.slice(match.index, match.index + match.length);
    const days = new Set<Weekday>();
    for (const word of matches(weekdayPattern, words)) {
      const weekday = weekdays[(word.groups[1] ?? "").toLowerCase()];
      if (weekday !== undefined) days.add(weekday);
    }
    if (days.size === 0) continue;
    const rule: RepeatRule = {
      interval: 1,
      unit: "week",
      afterCompletion: false,
      weekdays: [...days].sort((a, b) => a - b),
    };
    addRepeat(match, rule, firstDay(rule, today));
  }
  for (const match of matches(`(?:every|each|jeden|jede|jedes|alle)${WS}+(?:(${DIGIT}{1,2})${WS}+)?(${unitPattern})`, text)) {
    const interval = int(match.groups[1]) ?? 1;
    const by = unit(match.groups[2]);
    if (by !== undefined) addRepeat(match, { interval: Math.max(interval, 1), unit: by, afterCompletion: false }, undefined);
  }
  for (const match of matches("(daily|weekly|monthly|yearly|annually|täglich|wöchentlich|monatlich|jährlich)", text)) {
    const rule = dailyRules[(match.groups[1] ?? "").toLowerCase()];
    if (rule) addRepeat(match, { ...rule }, undefined);
  }

  // A day right after "every" is part of a repeat not read here ("Jeden Morgen joggen" is every morning, not
  // tomorrow), so it is no date either.
  const followsEvery = (match: Match) => afterEvery.test(text.slice(0, match.index));

  for (const [pattern, resolve] of datePatterns) {
    for (const match of matches(pattern, text)) {
      if (followsEvery(match)) continue;
      const start = resolve(match.groups, today);
      if (start === undefined) continue;
      let label: string;
      if (start.kind === "someday") label = somedayLabel(labels);
      else if (start.kind === "on" && start.day <= today) label = todayLabel(labels);
      else if (start.kind === "on") label = friendly(start.day, today, labels);
      else continue;
      add(match, { kind: "start", value: start, label });
    }
  }

  for (const [pattern, resolve] of timePatterns) {
    for (const match of matches(pattern, text)) {
      const time = resolve(match.groups);
      if (time === undefined) continue;
      const [hour, minute] = time;
      if (hour >= 24 || minute >= 60) continue;
      add(match, { kind: "time", value: { hour, minute }, label: timeLabel(hour, minute, labels) });
    }
  }

  for (const match of matches(`#([\\p{L}${DIGIT}_-]+)`, text, false)) {
    const typed = match.groups[1] ?? "";
    const tag = context.tags?.find((known) => sameIgnoringCase(known, typed)) ?? capitalizedFirst(typed);
    add(match, { kind: "tag", value: tag, label: tag });
  }

  for (const match of matches(`@([\\p{L}${DIGIT}_-]+)`, text, false)) {
    const typed = (match.groups[1] ?? "").toLowerCase().normalize("NFC");
    const contains = (title: string) => title.toLowerCase().normalize("NFC").includes(typed);
    const project = context.projects?.find((candidate) => !candidate.completed && contains(candidate.title));
    if (project) {
      add(match, { kind: "project", value: project.id, label: project.title });
      continue;
    }
    const area = context.areas?.find((candidate) => contains(candidate.title));
    if (area) add(match, { kind: "area", value: area.id, label: area.title });
  }

  // Remove the recognised words from back to front, so that the places stay valid.
  let title = text;
  for (const item of [...found].sort((a, b) => b.index - a.index)) {
    title = title.slice(0, item.index) + title.slice(item.index + item.length);
  }
  title = title.replace(runsOfSpace, " ").replace(edges, "");

  return { tokens: [...found].sort((a, b) => a.index - b.index).map((item) => item.token), title };
}

/**
 * A day typed into a picker, such as "fri", "tomorrow", "12.10." or "in 3 days", read as the title parser reads it
 * (QuickParser.day(in:) in TodoChoices.swift).
 */
export function dayIn(text: string, today: Day): Day | undefined {
  const trimmed = text.replace(/^[\p{Zs}\t]+|[\p{Zs}\t]+$/gu, "");
  if (trimmed === "") return undefined;
  for (const token of parse("x " + trimmed, { today }).tokens) {
    if (token.kind === "start" && token.value.kind === "on") return token.value.day;
    if (token.kind === "deadline") return token.value;
    if (token.kind === "repeats" && token.value.start !== undefined) return token.value.start;
  }
  return undefined;
}
